import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const CustomsClearanceForm = () => {
  const [formData, setFormData] = useState({
    order_id: '',
    documents_release_date: '',
    documents_handover_date: '',
    clearance_date: '',
    pq_status: '',
    fssai_status: '',
    bill_of_entry_no: '',
    bill_of_entry_date: '',
    duty_paid_usd: '',
    demurrage_amount: '',
    remarks: '',
    currency: '',
    fx_rate: '',
    warehouse_entry_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [importOrders, setImportOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch import orders for dropdown
    const fetchImportOrders = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setImportOrders(
            data.data.map(order => ({
              ...order,
              label: ` (${order.purchase_contract_no}) (${order.vessel_name})`,
              value: order.order_id
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching import orders:', err);
      }
    };

    fetchImportOrders();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      // Save customs clearance
      const res = await fetch(`${API_BASE_URL}/api/import/customs-clearance-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        // Create cost breakup entries based on duty_paid_usd and demurrage_amount
        const costBreakupPromises = [];
        
        // If duty_paid_usd > 0, create DUTY entry
        if (formData.duty_paid_usd && parseFloat(formData.duty_paid_usd) > 0) {
          const dutyPayload = {
            order_id: formData.order_id,
            component_type: 'DUTY',
            amount_inr: formData.duty_paid_usd,
            currency: formData.currency || 'INR',
            fx_rate: formData.fx_rate || 1,
            warehouse_entry_id: null,
            remarks: formData.remarks,
          };
          costBreakupPromises.push(
            fetch(`${API_BASE_URL}/api/cost/cost_breakup_post`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(dutyPayload),
            })
          );
        }
        
        // If demurrage_amount > 0, create DEMURRAGE entry
        if (formData.demurrage_amount && parseFloat(formData.demurrage_amount) > 0) {
          const demurragePayload = {
            order_id: formData.order_id,
            component_type: 'DEMURRAGE',
            amount_inr: formData.demurrage_amount,
            currency: formData.currency || 'INR',
            fx_rate: formData.fx_rate || 1,
            warehouse_entry_id: null,
            remarks: formData.remarks,
          };
          costBreakupPromises.push(
            fetch(`${API_BASE_URL}/api/cost/cost_breakup_post`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(demurragePayload),
            })
          );
        }
        
        // Execute all cost breakup requests
        if (costBreakupPromises.length > 0) {
          await Promise.all(costBreakupPromises);
        }
        
        setMessage({ type: 'success', text: data.message || 'Customs clearance saved.' });
        setFormData({
          order_id: '',
          documents_release_date: '',
          documents_handover_date: '',
          clearance_date: '',
          pq_status: '',
          fssai_status: '',
          bill_of_entry_no: '',
          bill_of_entry_date: '',
          duty_paid_usd: '',
          demurrage_amount: '',
          remarks: '',
          currency: '',
          fx_rate: '',
          warehouse_entry_id: '',
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <div className="flex justify-between items-center border-b pb-3 mb-5">
        <h2 className="text-lg font-medium text-gray-800">Customs Clearance Registration</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#3899cf] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      
      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Purchase Contract *</label>
            <SearchableDropdown
              options={importOrders}
              value={formData.order_id}
              onChange={val => setFormData(prev => ({ ...prev, order_id: val }))}
              placeholder="Select Import Order"
              displayKey="label"   // Use the combined label
              valueKey="order_id"
              required
              className=""
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Documents Release Date</label>
            <input
              type="date"
              name="documents_release_date"
              value={formData.documents_release_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Documents Handover Date</label>
            <input
              type="date"
              name="documents_handover_date"
              value={formData.documents_handover_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Clearance Date</label>
            <input
              type="date"
              name="clearance_date"
              value={formData.clearance_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">PQ Status</label>
            <select
              name="pq_status"
              value={formData.pq_status}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            >
              <option value="">Select Status</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="COMPLETED">COMPLETED</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">FSSAI Status</label>
            <select
              name="fssai_status"
              value={formData.fssai_status}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            >
              <option value="">Select Status</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="COMPLETED">COMPLETED</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Bill of Entry No</label>
            <input
              type="text"
              name="bill_of_entry_no"
              value={formData.bill_of_entry_no}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              placeholder="Enter Bill of Entry Number"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Bill of Entry Date</label>
            <input
              type="date"
              name="bill_of_entry_date"
              value={formData.bill_of_entry_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Duty Paid (USD)</label>
            <input
              type="number"
              step="0.01"
              name="duty_paid_usd"
              value={formData.duty_paid_usd}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Demurrage Amount</label>
            <input
              type="number"
              step="0.01"
              name="demurrage_amount"
              value={formData.demurrage_amount}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Cost Breakup Section */}
        <div className="border-t pt-4">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
                placeholder="INR"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">FX Rate</label>
              <input
                type="number"
                step="0.0001"
                name="fx_rate"
                value={formData.fx_rate}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
                placeholder="1.0000"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Remarks</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            rows="3"
            className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-blue-400 rounded-none"
            placeholder="Enter any additional remarks..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm bg-[#3899cf] text-white hover:bg-[#0e8dc6] transition disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Customs Clearance'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomsClearanceForm;
